import { useState, useMemo, useCallback, useContext } from 'react';
// import ReactEcharts from 'echarts-for-react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts';
import TypeChangeBtnGroup from '@/components/type_change_btn_group/TypeChangeBtnGroup';
import { ThemeContext } from 'styled-components';
import { BrandContext } from '@/contexts/ContextProviders';
import Head from '@/containers/chart_box/Head';
import Body from '@/containers/chart_box/Body';
import { transNum } from '@/utils/comUtil';
import { useIntl } from 'umi';
import dayjs from 'dayjs';
import { mulTimeDimensionMulIndexMulValue } from '@/utils/reportResponseUtil';

import {
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
} from 'echarts/components';
import { BarChart, LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  ToolboxComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
]);

// 图表容器样式与坐标轴格式化函数提到模块级，保证引用稳定，
// 避免 ReactEChartsCore 因为 props 引用变化而重复 setOption
const CHART_STYLE = { width: '100%', height: '100%' };
const fmtAxisTransNum = (p) => {
  return transNum(p);
};

const Point = (props) => {
  const themeContext = useContext(ThemeContext);
  const brandContext = useContext(BrandContext);
  const intl = useIntl();
  const { formatMessage } = intl;
  const { charts } = themeContext[brandContext];
  const { chartsFontSize, fontFamily } = themeContext.comm;
  const [type, setType] = useState('WEEK');
  const { data, title, loading } = props;

  const changeType = useCallback((v) => {
    setType(v);
  }, []);
  // 渲染阶段直接计算 option，图表挂载时拿到的就是完整配置，
  // 避免先挂载空 option 再由 effect 补一次而绘制两遍
  const chartOption = useMemo(() => {
    let xData = [];
    let series = [];
    let accPoint = [];
    let redPoint = [];
    let remainPoint = [];
    const result = mulTimeDimensionMulIndexMulValue(data || []);
    if (type === 'WEEK') {
      for (let index = 7; index > 0; index--) {
        xData.push(dayjs().subtract(index, 'day').format('YYYY-MM-DD'));
      }
    } else {
      for (let index = 30; index > 0; index--) {
        xData.push(dayjs().subtract(index, 'day').format('YYYY-MM-DD'));
      }
    }
    xData.forEach((e) => {
      const accPointItem = result[type]?.ACC_POINT?.find(
        (s) => s.LABEL === e,
      ) || { VALUE: 0 };
      accPoint.push(accPointItem.VALUE);
      const redPointItem = result[type]?.RED_POINT?.find(
        (s) => s.LABEL === e,
      ) || { VALUE: 0 };
      redPoint.push(Math.abs(Number(redPointItem.VALUE)));
      remainPoint.push(
        Number(accPointItem.VALUE) - Math.abs(Number(redPointItem.VALUE)),
      );
    });
    //   'ec.dashboard.n09.tip01':'',
    // 'ec.dashboard.n09.tip02':'',
    // 'ec.dashboard.n09.tip03':'',
    series.push({
      // name: '发放',
      name: formatMessage({ id: 'ec.dashboard.n09.tip01' }),
      type: 'bar',
      stack: 'total',
      data: accPoint,
    });
    series.push({
      // name: '扣减',
      name: formatMessage({ id: 'ec.dashboard.n09.tip02' }),
      type: 'bar',
      stack: 'total',
      data: redPoint,
    });
    series.push({
      // name: '余额',
      name: formatMessage({ id: 'ec.dashboard.n09.tip03' }),
      type: 'line',
      data: remainPoint,
      barWidth: '50%',
    });
    let option = {
      textStyle: {
        fontSize: chartsFontSize.s03,
        fontFamily: fontFamily,
      },
      tooltip: {
        trigger: 'axis',
        textStyle: {
          fontSize: chartsFontSize.s03,
        },
        confine: true,
      },
      legend: {
        // data: ['发放', '扣减', '余额'],
        data: [
          formatMessage({ id: 'ec.dashboard.n09.tip01' }),
          formatMessage({ id: 'ec.dashboard.n09.tip02' }),
          formatMessage({ id: 'ec.dashboard.n09.tip03' }),
        ],
        itemWidth: 10,
        itemHeight: 10,
        top: 0,
        // icon: 'rect',
      },
      grid: {
        top: 30,
        left: 10,
        right: 20,
        bottom: 0,
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: xData,
        axisLabel: {
          color: charts.xColor, //坐标值得具体的颜色
          fontSize: chartsFontSize.s03,
          //   interval: function () {
          //     return true;
          //   },
        },
        axisTick: false,
        axisLine: {
          lineStyle: {
            color: charts.axisLineColor,
          },
        },
      },
      yAxis: {
        type: 'value',
        axisTick: false,
        axisLine: false,
        axisLabel: {
          color: charts.yColor, //坐标值得具体的颜色
          fontSize: chartsFontSize.s03,
          formatter: fmtAxisTransNum,
        },
        splitLine: {
          show: true, //去除网格线
          lineStyle: {
            color: charts.axisLineColor, //坐标线的颜色
          },
        },
        // name: '人',
        // nameRotate: 45,
        nameGap: 15,
        nameTextStyle: {
          // color: color.black0100,
          padding: [0, 35, 0, 0],
        },
        min: 0,
      },
      // color: ['#931B20', '#2A5AE5', '#22C8FF'],
      color: ['#54ace9', '#3a51a8', '#22C8FF'],
      //       7.积分变化趋势：
      // ①发放：#3a51a8
      // ②扣减：#54ace9
      // ③余额：#FFFFFF
      series: series,
    };
    return option;
  }, [
    data,
    type,
    formatMessage,
    chartsFontSize.s03,
    fontFamily,
    charts.xColor,
    charts.axisLineColor,
    charts.yColor,
  ]);

  return (
    <>
      <Head boxId={title}>
        <TypeChangeBtnGroup
          typeList={[
            {
              label: `7${formatMessage({ id: 'comm.date.lbl.02' })}`,
              value: 'WEEK',
            },
            {
              label: `30${formatMessage({ id: 'comm.date.lbl.02' })}`,
              value: 'MONTH',
            },
          ]}
          change={changeType}
        />
      </Head>
      <Body bizData={data} loading={loading}>
        <ReactEChartsCore
          echarts={echarts}
          lazyUpdate={true}
          option={chartOption}
          notMerge={true}
          style={CHART_STYLE}
          showLoading={false}
        />
        {/* <ReactEcharts
          style={{ width: '100%', height: '100%' }}
          option={echartsOptions || {}}
          notMerge={true}
          lazyUpdate={true}
          showLoading={false}
          // onEvents={onEvents}
        /> */}
      </Body>
    </>
  );
};
export default Point;
